/**
 * RunContext.tsx
 * Global run-state manager — persists across pages and devices.
 *
 * ● Adaptive polling: 3 s while runs are active, 30 s when idle.
 * ● Immediate re-poll on: window focus, tab visibility, lyra:run-start / lyra:run-end
 * ● Provides isAgentRunning / isChainRunning helpers
 * ● stopRun(runId) aborts the HTTP call and marks the DB record stopped
 */
import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { agentRunAPI } from '../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────
export interface ActiveRun {
  _id: string;
  agentId: string | null;
  chainId: string | null;
  agentName: string | null;
  input: string;
  status: 'running';
  createdAt: string;
}

interface RunContextValue {
  /** All currently-running runs for this user (server-persisted) */
  activeRuns: ActiveRun[];
  /** True while the initial poll is in flight */
  loading: boolean;
  /** Register an AbortController for a specific runId so stopRun can cancel it */
  registerAbort: (runId: string, controller: AbortController) => void;
  /** Unregister after a run completes naturally */
  unregisterAbort: (runId: string) => void;
  /** Stop a run: abort HTTP + mark DB stopped */
  stopRun: (runId: string) => Promise<void>;
  /** Convenience: is any run for this agentId currently active? */
  isAgentRunning: (agentId: string) => boolean;
  /** Convenience: is any run for this chainId currently active? */
  isChainRunning: (chainId: string) => boolean;
  /** Get the active runId for a given agentId (for stop button) */
  getAgentRunId: (agentId: string) => string | null;
  /** Get the active runId for a given chainId */
  getChainRunId: (chainId: string) => string | null;
  /** Force an immediate refresh (after triggering a new run) */
  refresh: () => void;
}

const RunContext = createContext<RunContextValue | null>(null);

// Poll only while runs are active — 3s between checks
const POLL_ACTIVE_MS = 3000;
// Idle heartbeat — catch runs started from another tab/page even if event missed
const POLL_IDLE_MS = 15000;

// ── Provider ──────────────────────────────────────────────────────────────────
export const RunProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeRuns, setActiveRuns] = useState<ActiveRun[]>([]);
  const [loading, setLoading]       = useState(true);
  const abortMap      = useRef<Map<string, AbortController>>(new Map());
  const timerRef      = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef    = useRef(true);
  // Ref mirrors state so poll loop can read it without stale closures
  const activeRunsRef = useRef<ActiveRun[]>([]);

  // ── Fetch + smart reschedule ────────────────────────────────────────────────
  const fetchActive = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const data = await agentRunAPI.getActive();
      if (!mountedRef.current) return;
      const runs = data.runs || [];
      activeRunsRef.current = runs;
      setActiveRuns(runs);
    } catch {
      // silent — network errors shouldn't break UI
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Schedule the next active poll ONLY if there are active runs
  const scheduleNext = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (activeRunsRef.current.length === 0) return; // nothing running — stop polling
    timerRef.current = setTimeout(() => {
      fetchActive().then(scheduleNext);
    }, POLL_ACTIVE_MS);
  }, [fetchActive]);

  // Start fast polling (called when a new run is kicked off)
  // Polls immediately, then 1.5s later (catches server race), then continues at 3s
  const startPolling = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    // Immediate fetch
    fetchActive().then(() => {
      // 1.5s retry — server may not have created the AgentRun record yet on first fetch
      timerRef.current = setTimeout(() => {
        fetchActive().then(scheduleNext);
      }, 1500);
    });
  }, [fetchActive, scheduleNext]);

  // No regular idle polling when there are no active runs. We'll only poll
  // on-demand (startPolling) or when a run-start event occurs.

  useEffect(() => {
    mountedRef.current = true;

    // One-time check on mount. If there are active runs, start fast polling.
    fetchActive().then(() => {
      if (activeRunsRef.current.length > 0) scheduleNext();
    });

    // When a run starts: immediately fetch + begin fast polling
    const onRunStart = () => startPolling();
    window.addEventListener('lyra:run-start', onRunStart);

    // When a run ends: do one final fetch to clear state, then stop polling if empty
    const onRunEnd = async () => {
      await fetchActive();
      if (activeRunsRef.current.length === 0) {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      }
    };
    window.addEventListener('lyra:run-end', onRunEnd);

    // On tab focus / visibility: only re-check if runs are (or might be) active
    const onFocus = () => { if (activeRunsRef.current.length > 0) startPolling(); else fetchActive(); };
    window.addEventListener('focus', onFocus);
    const onVisible = () => { if (document.visibilityState === 'visible') onFocus(); };
    document.addEventListener('visibilitychange', onVisible);

    // Stop all active runs when RunDrawer's Stop button fires
    const onStopRequest = () => {
      setActiveRuns(current => {
        current.forEach(r => { agentRunAPI.stop(r._id).catch(() => {}); });
        return [];
      });
      activeRunsRef.current = [];
      if (timerRef.current) clearTimeout(timerRef.current);
      window.dispatchEvent(new CustomEvent('lyra:run-end'));
    };
    window.addEventListener('lyra:run-stop-request', onStopRequest);

    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
      window.removeEventListener('lyra:run-start', onRunStart);
      window.removeEventListener('lyra:run-end',   onRunEnd);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('lyra:run-stop-request', onStopRequest);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [fetchActive, scheduleNext, startPolling]);

  // ── Abort management ───────────────────────────────────────────────────────
  const registerAbort = useCallback((runId: string, controller: AbortController) => {
    abortMap.current.set(runId, controller);
  }, []);

  const unregisterAbort = useCallback((runId: string) => {
    abortMap.current.delete(runId);
  }, []);

  // ── Stop ───────────────────────────────────────────────────────────────────
  const stopRun = useCallback(async (runId: string) => {
    // 1. Abort in-flight HTTP request (if any)
    const ctrl = abortMap.current.get(runId);
    if (ctrl) { ctrl.abort(); abortMap.current.delete(runId); }

    // 2. Mark as stopped on server
    try { await agentRunAPI.stop(runId); } catch { /* ignore */ }

    // 3. Optimistically remove from local state
    setActiveRuns(prev => prev.filter(r => r._id !== runId));

    // 4. Fire event so Layout / ConstellationFAB update
    window.dispatchEvent(new CustomEvent('lyra:run-end', { detail: { runId } }));
  }, []);

  // ── Refresh ────────────────────────────────────────────────────────────────
  // Called after triggering a run — fetch immediately then poll if needed
  const refresh = useCallback(() => {
    startPolling();
  }, [startPolling]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const isAgentRunning = useCallback(
    (agentId: string) => activeRuns.some(r => r.agentId === agentId),
    [activeRuns]
  );

  const isChainRunning = useCallback(
    (chainId: string) => activeRuns.some(r => r.chainId === chainId),
    [activeRuns]
  );

  const getAgentRunId = useCallback(
    (agentId: string) => activeRuns.find(r => r.agentId === agentId)?._id ?? null,
    [activeRuns]
  );

  const getChainRunId = useCallback(
    (chainId: string) => activeRuns.find(r => r.chainId === chainId)?._id ?? null,
    [activeRuns]
  );

  return (
    <RunContext.Provider value={{
      activeRuns, loading,
      registerAbort, unregisterAbort, stopRun, refresh,
      isAgentRunning, isChainRunning, getAgentRunId, getChainRunId,
    }}>
      {children}
    </RunContext.Provider>
  );
};

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useRun(): RunContextValue {
  const ctx = useContext(RunContext);
  if (!ctx) throw new Error('useRun must be used inside <RunProvider>');
  return ctx;
}

export default RunContext;
